import React from 'react';
import {
  Box, Button, Chip, FormControlLabel, IconButton, Paper, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography, useTheme
} from '@mui/material';
import {
  Add as AddIcon, Archive as ArchiveIcon, Edit as EditIcon, AdminPanelSettings as RolesIcon
} from '@mui/icons-material';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { describeMatrix } from '../../utils/permissions';

const f = { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' };

/**
 * RolesTab — list of the typing center's custom roles with edit / archive actions.
 * Archive is blocked (with the reason) while any member still holds the role, mirroring
 * the backend rule.
 *
 * Props: roles, users, showArchived, onToggleArchived, onCreate, onEdit, onArchive
 */
const RolesTab = ({ roles = [], users = [], showArchived, onToggleArchived, onCreate, onEdit, onArchive }) => {
  const theme = useTheme();
  const assignedCount = (roleId) => users.filter((u) => (u.role_id || u.roleId) === roleId).length;
  const visible = roles.filter((r) => showArchived || r.status !== 'ARCHIVED');

  if (roles.length === 0) {
    return (
      <Paper sx={{ py: 5, textAlign: 'center' }}>
        <RolesIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
        <Typography sx={{ fontSize: f.sm, fontWeight: 600 }}>No roles yet</Typography>
        <Typography sx={{ fontSize: f.xs, color: 'text.secondary', mb: 2, maxWidth: 420, mx: 'auto' }}>
          A role decides what a team member can see and do. Create one, then assign it when you add a member.
        </Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={onCreate} sx={{ fontSize: f.xs }}>
          Create your first role
        </Button>
      </Paper>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <FormControlLabel
          control={<Switch size="small" checked={showArchived} onChange={(e) => onToggleArchived(e.target.checked)} />}
          label={<Typography sx={{ fontSize: f.xs }}>Show archived</Typography>}
        />
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={getBrandedTableHeadSx(theme)}>
            <TableRow sx={{ '& th': { fontSize: f.xs } }}>
              <TableCell>Role</TableCell>
              <TableCell>Access</TableCell>
              <TableCell align="center">Members</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((role) => {
              const count = assignedCount(role.id);
              const archived = role.status === 'ARCHIVED';
              return (
                <TableRow key={role.id} hover sx={{ opacity: archived ? 0.6 : 1 }}>
                  <TableCell>
                    <Typography sx={{ fontSize: f.sm, fontWeight: 600 }}>{role.name}</Typography>
                    {role.description ? (
                      <Typography sx={{ fontSize: f.xs, color: 'text.secondary' }}>{role.description}</Typography>
                    ) : (
                      <Typography sx={{ fontSize: f.xs, color: 'text.secondary', fontFamily: 'monospace' }}>{role.code}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: f.xs }}>{describeMatrix(role.permissions)}</TableCell>
                  <TableCell align="center" sx={{ fontSize: f.xs }}>{count}</TableCell>
                  <TableCell align="center">
                    <Chip label={archived ? 'Archived' : 'Active'} size="small"
                      color={archived ? 'default' : 'success'} sx={{ fontSize: '0.6rem', height: 18 }} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {!archived && (
                      <>
                        <Tooltip title="Edit role">
                          <IconButton size="small" onClick={() => onEdit(role)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title={count > 0
                          ? `Assigned to ${count} member${count === 1 ? '' : 's'} — reassign them first`
                          : 'Archive role'}>
                          <span>
                            <IconButton size="small" color="error" disabled={count > 0} onClick={() => onArchive(role)}>
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RolesTab;
